import { useLDClient } from "launchdarkly-react-client-sdk";
import { useEffect } from "react";

interface ReportTemplateLike {
  name?: string | null;
  exportFramework?: string | null;
  payorType?: string | null;
}

/**
 * Layers a `reportTemplate` context kind into the active LaunchDarkly
 * context whenever the user is viewing a specific report. Lets flag
 * targeting rules in the LD dashboard match on `reportTemplate.key`
 * (template name), `reportTemplate.exportFramework`, or
 * `reportTemplate.payorType` so per-template flags can be
 * rolled out without an org-wide kill switch.
 *
 * Strips the `reportTemplate` kind on unmount / template change so flag
 * reads outside of report pages aren't evaluated against a stale template.
 *
 * Call from the root of each report detail provider (clinical, assessment
 * v2, evaluation) — anywhere a single report is the active subject.
 */
export const useLDReportTemplateContext = (reportTemplate: ReportTemplateLike | null | undefined): void => {
  const ldClient = useLDClient();
  const templateName = reportTemplate?.name ?? "";
  const exportFramework = reportTemplate?.exportFramework ?? "";
  const payorType = reportTemplate?.payorType ?? "";

  useEffect(() => {
    if (!ldClient || !templateName) return;

    // The base context was set by FeatureFlagProvider as a single `user`
    // kind. On subsequent navigations within a session it may already be a
    // `multi` context; in either case the `user` partition is what we want
    // to preserve.
    const current = ldClient.getContext() as Record<string, unknown>;
    const userPart = current?.kind === "multi" ? (current.user as Record<string, unknown>) : current;

    void ldClient.identify({
      kind: "multi",
      user: userPart,
      reportTemplate: {
        kind: "reportTemplate",
        key: templateName,
        exportFramework,
        payorType,
      },
    });

    return () => {
      // Restore the user-only context so flag reads outside the report
      // surface (nav, dashboards, etc.) aren't evaluated against a stale
      // reportTemplate.
      void ldClient.identify(userPart);
    };
  }, [ldClient, templateName, exportFramework, payorType]);
};
